import React, { useState } from 'react';
import { Typography, Box } from '@mui/material';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import {
  AlertTemplateCard,
  AlertTemplateGrid,
  AlertSearchInput
} from './AlertTemplateCard';
import {
  AlertTemplateType,
  BeaconAlertTemplates,
  AtlasAlertTemplates
} from '../schema/template';
import { getAppName } from 'src/utils/app';
import { AppName } from 'sl-api-connector';

interface AlertTemplatesContentProps {
  onTemplateSelect?: (template: AlertTemplateType) => void;
}

const AlertTemplatesTab: React.FC<AlertTemplatesContentProps> = ({ onTemplateSelect }) => {
  const [selectedTemplate, setSelectedTemplate] = useState<AlertTemplateType | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');

  const currentAppName = getAppName();
  const allAlertTemplates = currentAppName === AppName.Atlas ? AtlasAlertTemplates : BeaconAlertTemplates;

  const handleTemplateClick = (template: AlertTemplateType) => {
    if (!template.valid) return; // Don't allow clicking disabled templates
    setSelectedTemplate(template);
    if (onTemplateSelect) {
      onTemplateSelect(template);
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  // Filter templates based on search query
  const filteredTemplates = allAlertTemplates.filter((template) => {
    const query = searchQuery.toLowerCase();
    return (
      template.title.toLowerCase().includes(query) ||
      template.description.toLowerCase().includes(query) ||
      template.tags.some((tag) => tag.toLowerCase().includes(query))
    );
  });

  return (
    <Flex flexDirection="column" gap={32}>
      <Flex flexDirection="column">
        <Text variant="h4" lineHeight="28px">
          Templates
        </Text>
        <Flex marginTop="8px" fontSize={16} lineHeight="24px">
          Get a head start with ready-to-use templates tailored for common use cases, or create your own alert from scratch.
        </Flex>
      </Flex>
      <Flex flexDirection="column" gap={32}>
        <AlertSearchInput 
          placeholder="Search templates" 
          value={searchQuery} 
          onChange={handleSearchChange} 
        />
        <AlertTemplateGrid>
          {filteredTemplates.map((template) => (
            <AlertTemplateCard
              key={template.id}
              template={template}
              isSelected={selectedTemplate?.id === template.id}
              onSelect={handleTemplateClick}
              disabled={!template.valid}
            />
          ))}
        </AlertTemplateGrid>

        {/* No results message */}
        {filteredTemplates.length === 0 && searchQuery && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body2" color="text.secondary">
              No templates found for &quot;{searchQuery}&quot;
            </Typography>
          </Box>
        )}
      </Flex>
    </Flex>
  );
};

export default AlertTemplatesTab;
